import type { ReactNode } from "react";
import ScheduleOutlinedIcon from "@mui/icons-material/ScheduleOutlined";
import LanguageOutlinedIcon from "@mui/icons-material/LanguageOutlined";
import FileDownloadOutlinedIcon from "@mui/icons-material/FileDownloadOutlined";
import { useTranslation } from "react-i18next";
import RoundedContentBox from "@/components/general/roundedContentBox";
import { KaerIcon } from "@/icons/kaerIcon";
import { LmsStatusPill } from "@/components/lms/lmsStatusChip";
import type { LmsStatusTone } from "@/components/lms/lmsStatusTone";
import GenericButton from "@/components/forms/inputs/button/genericButton";
import { getLmsCourseCategoryPresentation } from "@/components/lms/lmsCourseCategoryPresentation";

export type LmsCourseCardTone = LmsStatusTone;

export interface LmsCourseCardData {
  id: string;
  enrolmentId?: string;
  courseId?: string;
  title: string;
  category: {
    id?: string;
    name: string;
  };
  language?: string;
  languageCode?: string;
  availableLanguageCodes?: string[];
  durationLabel: string;
  imageUrl?: string | null;
  topRightLabel?: string;
  bottomStatusLabel?: string;
  bottomStatusTone?: LmsCourseCardTone;
  canDownloadCertificate?: boolean;
}

export interface LmsCourseCardPrimaryAction {
  label: string;
  onClick: () => void;
  startIcon?: ReactNode;
}

interface Props {
  course: LmsCourseCardData;
  onClick?: () => void;
  onDownloadCertificate?: (enrolmentId: string) => void;
  primaryAction?: LmsCourseCardPrimaryAction;
  dataTestId?: string;
}

export default function LmsCourseCard({
  course,
  onClick,
  onDownloadCertificate,
  primaryAction,
  dataTestId = "lms-course-card",
}: Props) {
  const { t } = useTranslation();
  const languageDisplay = course.availableLanguageCodes?.length
    ? course.availableLanguageCodes.join(", ")
    : course.languageCode;

  return (
    <div
      data-testid={dataTestId}
      data-course-title={course.title}
      style={{ height: "100%" }}
    >
      <RoundedContentBox
        onClick={onClick}
        enableHover={Boolean(onClick)}
        showCursor={Boolean(onClick)}
        padding="0"
        boxShadow="0 4px 16px -6px rgba(32, 30, 30, 0.08)"
        hoverBoxShadow="0 16px 40px -12px rgba(140, 30, 200, 0.2), 0 8px 20px -8px rgba(32, 30, 30, 0.1), 0 2px 8px rgba(32, 30, 30, 0.06)"
        style={{
          height: "100%",
          display: "flex",
          flexDirection: "column",
          border: "1px solid #f0ebf5",
        }}
      >
        <div
          style={{
            position: "relative",
            height: "9.75rem",
            background: course.imageUrl
              ? `center / cover no-repeat url(${course.imageUrl})`
              : "linear-gradient(135deg, #4a1a89 0%, #8c1ec8 55%, #b46be2 100%)",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
          }}
        >
          {!course.imageUrl && (
            <KaerIcon icon="lms" width="64" height="64" color="#ffffff" />
          )}
          {course.topRightLabel && (
            <span
              style={{
                position: "absolute",
                top: "0.75rem",
                right: "0.75rem",
                padding: "0.2rem 0.6rem",
                borderRadius: "999px",
                backgroundColor: "#ffffff",
                color: "#7d243a",
                fontSize: "0.75rem",
                fontWeight: 600,
                boxShadow: "0 4px 12px rgba(32, 30, 30, 0.12)",
              }}
            >
              {course.topRightLabel}
            </span>
          )}
        </div>
        <div
          style={{
            display: "flex",
            flexDirection: "column",
            gap: "0.875rem",
            padding: "1rem 1rem 1.125rem",
            flex: 1,
          }}
        >
          <div
            style={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              gap: "0.75rem",
              color: getLmsCourseCategoryPresentation(course.category).color,
              fontSize: "0.8125rem",
            }}
          >
            <span
              style={{
                minWidth: 0,
                overflow: "hidden",
                textOverflow: "ellipsis",
                whiteSpace: "nowrap",
              }}
            >
              {course.category.name}
            </span>
            <div
              style={{
                display: "flex",
                alignItems: "center",
                gap: "0.75rem",
                flexShrink: 0,
              }}
            >
              {languageDisplay && (
                <span
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: "0.2rem",
                  }}
                >
                  <LanguageOutlinedIcon sx={{ fontSize: "0.95rem" }} />
                  {languageDisplay}
                </span>
              )}
              <span
                style={{ display: "flex", alignItems: "center", gap: "0.2rem" }}
              >
                <ScheduleOutlinedIcon sx={{ fontSize: "0.95rem" }} />
                {course.durationLabel}
              </span>
            </div>
          </div>
          <p
            data-testid="lms-course-card-title"
            style={{
              margin: 0,
              color: "#201e1e",
              fontSize: "1.1rem",
              fontWeight: 500,
              lineHeight: 1.4,
              display: "-webkit-box",
              WebkitLineClamp: 2,
              WebkitBoxOrient: "vertical",
              overflow: "hidden",
            }}
          >
            {course.title}
          </p>
          <div style={{ marginTop: "auto" }}>
            {course.bottomStatusLabel && (
              <LmsStatusPill
                tone={course.bottomStatusTone ?? "neutral"}
                label={course.bottomStatusLabel}
              />
            )}
            {course.canDownloadCertificate &&
              course.enrolmentId &&
              onDownloadCertificate && (
                <div style={{ marginTop: "0.65rem" }}>
                  <GenericButton
                    variant="outlined"
                    startIcon={<FileDownloadOutlinedIcon />}
                    onClick={(event: any) => {
                      event?.stopPropagation?.();
                      onDownloadCertificate(course.enrolmentId as string);
                    }}
                    style={{ width: "100%", justifyContent: "center" }}
                    data-testid="lms-course-card-certificate-download-button"
                  >
                    {t("lms_download_certificate")}
                  </GenericButton>
                </div>
              )}
            {primaryAction && (
              <div style={{ marginTop: "0.65rem" }}>
                <GenericButton
                  startIcon={primaryAction.startIcon}
                  onClick={(event) => {
                    event.stopPropagation();
                    primaryAction.onClick();
                  }}
                  style={{ width: "100%", justifyContent: "center" }}
                  data-testid="lms-course-card-primary-action"
                >
                  {primaryAction.label}
                </GenericButton>
              </div>
            )}
          </div>
        </div>
      </RoundedContentBox>
    </div>
  );
}
